import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTenantMutation, TENANT_KEYS } from "../../hooks/useTenants";
import type { Tenant, DomainOperationResult } from "../../services/tenantApi";
import { DomainOperationAlert } from "./DomainOperationAlert";

/**
 * Props for CustomDomainsManager.
 * @param tenant - Current tenant whose custom domains are managed.
 */
interface CustomDomainsManagerProps {
  tenant: Tenant;
}

/**
 * Manages a tenant's custom domains: renders list, add/remove UI, and shows
 * DomainOperationAlert after each backend operation (pessimistic).
 * Domain operations state lives locally here (lifecycle per spec B4).
 */
export const CustomDomainsManager: React.FC<CustomDomainsManagerProps> = ({
  tenant,
}) => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateTenantMutation();

  const [newDomain, setNewDomain] = useState("");
  const [domainInputError, setDomainInputError] = useState<string | null>(null);
  const [domainOperations, setDomainOperations] = useState<
    DomainOperationResult[] | null
  >(null);

  const isLoading = updateMutation.isPending;

  const validateDomain = (domain: string): boolean => {
    return /^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i.test(domain);
  };

  const handleAddDomain = (): void => {
    const trimmed = newDomain.trim();
    if (!trimmed) return;

    if (!validateDomain(trimmed)) {
      setDomainInputError("Nieprawidłowy format domeny");
      return;
    }

    if ((tenant.customDomains || []).includes(trimmed)) {
      setDomainInputError("Ta domena jest już na liście");
      return;
    }

    setDomainInputError(null);
    const updatedDomains = [...(tenant.customDomains || []), trimmed];

    // Clear previous results when starting new submit (B4)
    setDomainOperations(null);

    updateMutation.mutate(
      {
        tenantId: tenant.id,
        tenantData: { customDomains: updatedDomains },
      },
      {
        onSuccess: (result) => {
          setNewDomain("");
          queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenant.id) });
          // Show domain operation results if available
          if (result && "domainOperations" in result && result.domainOperations) {
            setDomainOperations(result.domainOperations);
          }
        },
        // On error: useUpdateTenantMutation shows toast, form retains state
      }
    );
  };

  const handleRemoveDomain = (domain: string): void => {
    const updatedDomains = (tenant.customDomains || []).filter(
      (d) => d !== domain
    );

    // Clear previous results when starting new submit (B4)
    setDomainOperations(null);

    updateMutation.mutate(
      {
        tenantId: tenant.id,
        tenantData: { customDomains: updatedDomains },
      },
      {
        onSuccess: (result) => {
          queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenant.id) });
          if (result && "domainOperations" in result && result.domainOperations) {
            setDomainOperations(result.domainOperations);
          }
        },
      }
    );
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-neutral-700">
        Custom Domains
      </label>

      {/* Domain operation alert (B4) */}
      <DomainOperationAlert
        operations={domainOperations}
        onClose={() => setDomainOperations(null)}
      />

      {/* Input for new domain */}
      <div className="flex space-x-2">
        <div className="flex-1">
          <label htmlFor="new-custom-domain" className="sr-only">
            Dodaj domenę
          </label>
          <input
            id="new-custom-domain"
            type="text"
            value={newDomain}
            onChange={(e) => {
              setNewDomain(e.target.value);
              if (domainInputError) setDomainInputError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
            disabled={isLoading}
            placeholder="example.com"
            aria-invalid={!!domainInputError}
            aria-describedby={domainInputError ? "domain-error" : undefined}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              domainInputError ? "border-red-400" : "border-neutral-300"
            }`}
          />
          {domainInputError && (
            <p id="domain-error" role="alert" className="text-xs text-red-600 mt-1">{domainInputError}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleAddDomain}
          disabled={isLoading || !newDomain.trim()}
          className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50 hover:bg-primary-700 transition-colors"
        >
          Dodaj
        </button>
      </div>

      {/* Domain list */}
      {(tenant.customDomains || []).length > 0 && (
        <div className="border border-neutral-200 rounded-md divide-y">
          {(tenant.customDomains || []).map((domain) => (
            <div
              key={domain}
              className="flex items-center justify-between px-3 py-2"
            >
              <span className="font-mono text-sm">{domain}</span>
              <button
                type="button"
                onClick={() => handleRemoveDomain(domain)}
                disabled={isLoading}
                aria-label={`Usuń domenę ${domain}`}
                className="text-red-600 hover:text-red-800 text-sm transition-colors disabled:opacity-50"
              >
                Usuń
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-neutral-500">
        Po dodaniu domeny zostanie ona automatycznie skonfigurowana w CapRover.
        Upewnij się, że rekord DNS wskazuje na serwer CapRover.
      </p>
    </div>
  );
};

export default CustomDomainsManager;
